import React, { useEffect, useState } from "react";
import DashboardLayout from "../../layouts/Dashboard";
import { dashboardCRUD } from "../../http";
import { monthStart, monthEnd } from "../../functions/dateHelpers";
import FilterDatePicker from "../../components/DatePicker/FilterDatePiecker";
import { Button, Stack, Grid } from "@mui/material";
import { Divider } from "antd";
import { LoadingButton } from "@mui/lab";
import {sum} from "../../functions/linqSimulation";
import { roundTo2 } from "../../functions/formatters";
import XLSX from "xlsx-js-style";
import { formatDate, MOMENT_FORMAT_DATE_DMYHM_DOTS } from "../../functions/formatters";
import { filialCRUD, filialDivisionCRUD, departmentCRUD } from "../../http";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { organizationIds } from "../../models/organizationIds";
import { ContractSelect } from "../../components/Select/domainSelects";

// Функция для безопасного форматирования чисел
const safeRoundTo2 = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
        return 0;
    }
    return roundTo2(value);
};

const ResolutionsSummary = () => {
    const start = monthStart();
    const end = monthEnd();
    
    const [query, setQuery] = useState({
        paging: { skip: 0, take: 1000 },
        filter: {
            resolution: {
                createdAt: {
                    operand1: start,
                    operand2: end,
                    operator: "between"
                }
            }
        }
    });

    const [filters, setFilters] = useState({
        governmentContractId: null,
        filialId: null,
        filialDivisionId: null,
        departmentId: null
    });

    const [filials, setFilials] = useState([]);
    const [filialDivisions, setFilialDivisions] = useState([]);
    const [departments, setDepartments] = useState([]);

    const [data, setData] = useState([]);
    const [groupedData, setGroupedData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const fetchAsync = async () => {
        setLoading(true);        

        // Добавляем фильтры к запросу
        const requestQuery = { ...query };
        
        if (filters.governmentContractId) {
            requestQuery.filter.governmentContractId = {
                operand1: filters.governmentContractId,
                operator: "equals"
            };
        }

        if (filters.filialId) {
            requestQuery.filter.filialId = {
                operand1: filters.filialId,
                operator: "equals"
            };
        }
        
        if (filters.filialDivisionId) {
            requestQuery.filter.filialDivisionId = {
                operand1: filters.filialDivisionId,
                operator: "equals"
            };
        }
        
        if (filters.departmentId) {
            requestQuery.filter.departmentId = {
                operand1: filters.departmentId,
                operator: "equals"
            };
        }

        const response = await dashboardCRUD.post('resolutions/quantity', requestQuery);
        setData(response);

        // Группируем данные по указанным полям
        const grouped = groupDataByFields(response);
        setGroupedData(grouped);

        setLoading(false);
    }

    // Функция для группировки данных по полям
    const groupDataByFields = (data) => {
        const groups = {};
        
        data.forEach(item => {
            const key = `${item.filialName || ''}_${item.filialDivisionName || ''}_${item.createdAt || ''}_${item.resolutionNumber || ''}_${item.actNumber || ''}`;
            
            if (!groups[key]) {
                groups[key] = {
                    governmentContractName: item.governmentContractName || '',
                    filialName: item.filialName || '',
                    filialDivisionName: item.filialDivisionName || '',
                    createdAt: item.createdAt,
                    resolutionNumber: item.resolutionNumber || '',
                    actNumber: item.actNumber || '',
                    signedProtocols: 0,
                    notSignedProtocols: 0,
                    departmentName: item.departmentName || '',
                    quantityByRequest: 0,
                    quantityByTarification: 0,
                    sum: 0,
                    indicatorsCount: item.indicatorsCount || 0,
                    requests: [] // Массив для хранения всех заявок в группе
                };
            }
            
            // Добавляем заявку в группу
            groups[key].requests.push({
                number: item.number || 0,
                departmentName: item.departmentName || '',
                quantityByRequest: item.quantityByRequest || 0,
                quantityByTarification: item.quantityByTarification || 0,
                signedProtocols: item.signedProtocols || 0,
                notSignedProtocols: item.notSignedProtocols || 0,
                sum: item.sum || 0
            });
            
            // Суммируем числовые поля
            groups[key].signedProtocols += item.signedProtocols || 0;
            groups[key].notSignedProtocols += item.notSignedProtocols || 0;
            groups[key].quantityByRequest += item.quantityByRequest || 0;
            groups[key].quantityByTarification += item.quantityByTarification || 0;
            groups[key].sum += item.sum || 0;
        });
        
        return Object.values(groups);
    };

    const handleExcelExport = async () => {
        if (groupedData.length === 0) {
            alert('Нет данных для экспорта. Сначала сформируйте отчет.');
            return;
        }

        setExportLoading(true);

        try {
            const thinBorder = {style: 'thin', color: 'auto'};
            const thinBorders = {top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder};
            const thickBorder = {style: 'medium', color: 'auto'};
            const thickBorders = {top: thickBorder, bottom: thickBorder, left: thickBorder, right: thickBorder};
            const center10 = {alignment: {horizontal: 'center'}, font: {sz: 10}};
            const centerBold10 = {alignment: {horizontal: 'center'}, font: {sz: 10, bold: true}};
            const centerBold10_2 = {
                alignment: {horizontal: 'center', vertical: 'center', wrapText: true},
                border: thinBorders,
                font: {sz: 9, bold: true}
            };

            const wb = XLSX.utils.book_new();

            const cell = (text, style = center10) => {
                return {
                    t: "s",
                    v: text,
                    s: style
                };
            }

            const numberCell = (value, style = center10) => {
                return {
                    t: "n",
                    v: value,
                    s: style
                };
            }

            // Создаем заголовки таблицы на основе шаблона из Excel файла
            const aoa = [
                [
                    cell("№", centerBold10),
                    cell("Договор", centerBold10),
                    cell("Филиал", centerBold10),
                    cell("Районное отделение", centerBold10),
                    cell("Дата создания", centerBold10),
                    cell("Номер постановления", centerBold10),
                    cell("Количество показателей в постановлении", centerBold10),
                    cell("АКТ", centerBold10),
                    cell("Количество протокола", centerBold10),
                    cell("Количество неподписанных протоколов", centerBold10),
                    cell("Лаборатория", centerBold10),
                    cell("Заявка", centerBold10),
                    cell("Количество исследований", centerBold10),
                    cell("Количество исследований в тарификаторе", centerBold10),
                    cell("Сумма", centerBold10)
                ]
            ];

            // Добавляем данные
            let rowIndex = 1;
            groupedData.forEach((group) => {
                // Добавляем строки для каждой заявки в группе
                group.requests.forEach((request, requestIndex) => {
                    aoa.push([
                        cell(requestIndex === 0 ? rowIndex.toString() : ""), // Номер только для первой строки группы
                        cell(requestIndex === 0 ? (group.governmentContractName || "") : ""), // Договор только для первой строки
                        cell(requestIndex === 0 ? (group.filialName || "") : ""), // Филиал только для первой строки
                        cell(requestIndex === 0 ? (group.filialDivisionName || "") : ""), // Районное отделение только для первой строки
                        cell(requestIndex === 0 ? (group.createdAt ? formatDate(group.createdAt, "DD.MM.YYYY") : "") : ""), // Дата только для первой строки
                        cell(requestIndex === 0 ? (group.resolutionNumber || "") : ""), // Номер постановления только для первой строки
                        numberCell(requestIndex === 0 ? (group.indicatorsCount || 0) : 0), // Количество показателей в постановлении только для первой строки
                        cell(requestIndex === 0 ? (group.actNumber || "") : ""), // АКТ только для первой строки
                        numberCell(request.signedProtocols || 0),
                        numberCell(request.notSignedProtocols || 0),
                        cell(request.departmentName || ""),
                        cell(request.number != null ? String(request.number) : ""),
                        numberCell(request.quantityByRequest || 0),
                        numberCell(request.quantityByTarification || 0),
                        numberCell(safeRoundTo2(request.sum || 0))
                    ]);
                });
                rowIndex++;
            });

            // Добавляем итоговую строку
            if (groupedData.length > 0) {
                const totalProtocolCount = sum(groupedData, _ => _.signedProtocols || 0);
                const totalUnsignedProtocolCount = sum(groupedData, _ => _.notSignedProtocols || 0);
                const totalResearchCount = sum(groupedData, _ => _.quantityByRequest || 0);
                const totalTarificationResearchCount = sum(groupedData, _ => _.quantityByTarification || 0);
                const totalSum = safeRoundTo2(sum(groupedData, _ => _.sum || 0));
                
                aoa.push([
                    cell(""),
                    cell(""),
                    cell("Всего:", centerBold10),
                    cell(""),
                    cell(""),
                    cell(""),
                    cell(""),
                    cell(""),
                    numberCell(totalProtocolCount),
                    numberCell(totalUnsignedProtocolCount),
                    cell(""),
                    cell(""),
                    numberCell(totalResearchCount),
                    numberCell(totalTarificationResearchCount),
                    numberCell(totalSum)
                ]);
            }

            const ws = XLSX.utils.aoa_to_sheet(aoa);

            // Устанавливаем ширину колонок
            ws['!cols'] = [
                { width: 5 },   // №
                { width: 20 },  // Договор
                { width: 20 },  // Филиал
                { width: 25 },  // Районное отделение
                { width: 15 },  // Дата создания
                { width: 20 },  // Номер постановления
                { width: 25 },  // Количество показателей в постановлении
                { width: 15 },  // АКТ
                { width: 20 },  // Количество протокола
                { width: 25 },  // Количество неподписанных протоколов
                { width: 20 },  // Лаборатория
                { width: 15 },  // Заявка
                { width: 20 },  // Количество исследований
                { width: 25 },  // Количество исследований в тарификаторе
                { width: 15 }   // Сумма
            ];

            XLSX.utils.book_append_sheet(wb, ws, "Исполнение КСЭК Постановления");

            const fileName = `Исполнение_КСЭК_Постановления_${formatDate(new Date(), MOMENT_FORMAT_DATE_DMYHM_DOTS)}.xlsx`;
            XLSX.writeFile(wb, fileName);

        } catch (error) {
            console.error('Ошибка при экспорте:', error);
            alert('Ошибка при экспорте файла');
        } finally {
            setExportLoading(false);
        }
    }

    useEffect(() => {
        // Загружаем данные для фильтров при монтировании компонента
        const loadData = async () => {
            try {
                const [filialsData, departmentsData] = await Promise.all([
                    filialCRUD.search({ paging: { skip: 0, take: 1000 }, filter: { organizationId: { operand1: organizationIds.nce, operator: "equals" } } }),
                    departmentCRUD.search({ paging: { skip: 0, take: 1000 } })
                ]);
                
                setFilials(filialsData?.result || []);
                setDepartments(departmentsData?.result || []);
            } catch (error) {
                console.error('Ошибка загрузки данных фильтров:', error);
            }
        };
        
        loadData();
    }, []);

    // Загружаем районные отделения при изменении филиала
    useEffect(() => {
        const loadFilialDivisions = async () => {
            if (filters.filialId) {
                try {
                    const filialDivisionsData = await filialDivisionCRUD.search({ 
                        paging: { skip: 0, take: 1000 }, 
                        filter: { filialId: { operand1: filters.filialId, operator: "equals" } } 
                    });
                    setFilialDivisions(filialDivisionsData?.result || []);
                } catch (error) {
                    console.error('Ошибка загрузки районных отделений:', error);
                }
            } else {
                setFilialDivisions([]);
            }
        };
        
        loadFilialDivisions();
    }, [filters.filialId]);

    return (
        <DashboardLayout>
            <div style={{ padding: "20px" }}>
                <h2>Исполнение КСЭК Постановления</h2>
                <Divider />
                <Grid container spacing={2} style={{ marginBottom: "20px" }}>
                    <Grid item xs={12} md={2} style={{ marginTop: -8 }}>
                        <ContractSelect
                            label={"Договор КСЭК"}
                            year={(new Date()).getFullYear()}
                            fullWidth
                            margin="dense"
                            value={filters.governmentContractId}
                            onChange={(val) => setFilters({ ...filters, governmentContractId: val })}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Филиал</InputLabel>
                            <Select
                                value={filters.filialId || ''}
                                onChange={(e) => {
                                    setFilters({
                                        ...filters,
                                        filialId: e.target.value,
                                        filialDivisionId: null, // Сбрасываем районное отделение при смене филиала
                                        departmentId: null
                                    });
                                }}
                            >
                                <MenuItem value="">Все филиалы</MenuItem>
                                {filials.map((filial) => (
                                    <MenuItem key={filial.id} value={filial.id}>
                                        {filial.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Районное отделение</InputLabel>
                            <Select
                                value={filters.filialDivisionId || ''}
                                onChange={(e) => {
                                    setFilters({
                                        ...filters,
                                        filialDivisionId: e.target.value
                                    });
                                }}
                            >
                                <MenuItem value="">Все районные отделения</MenuItem>
                                {filialDivisions.map((division) => (
                                    <MenuItem key={division.id} value={division.id}>
                                        {division.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Лаборатория</InputLabel>
                            <Select
                                value={filters.departmentId || ''}
                                onChange={(e) => {
                                    setFilters({
                                        ...filters,
                                        departmentId: e.target.value
                                    });
                                }}
                            >
                                <MenuItem value="">Все лаборатории</MenuItem>
                                {departments.map((department) => (
                                    <MenuItem key={department.id} value={department.id}>
                                        {department.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <div>
                            <FilterDatePicker
                                label="Дата создания с"
                                value={query.filter.resolution.createdAt.operand1}
                                handleFunction={(date) => {
                                    setQuery({
                                        ...query,
                                        filter: {
                                            ...query.filter,
                                            resolution: {
                                                ...query.filter.resolution,
                                                createdAt: {
                                                    ...query.filter.resolution.createdAt,
                                                    operand1: date
                                                }
                                            }
                                        }
                                    });
                                }}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <div>
                            <FilterDatePicker
                                label="Дата создания по"
                                value={query.filter.resolution.createdAt.operand2}
                                handleFunction={(date) => {
                                    setQuery({
                                        ...query,
                                        filter: {
                                            ...query.filter,
                                            resolution: {
                                                ...query.filter.resolution,
                                                createdAt: {
                                                    ...query.filter.resolution.createdAt,
                                                    operand2: date
                                                }
                                            }
                                        }
                                    });
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
                <Stack direction="row" spacing={2} style={{ marginBottom: "20px" }}>
                    <Button
                        variant="contained"
                        onClick={fetchAsync}
                        disabled={loading}
                    >
                        {loading ? "Загрузка..." : "Обновить"}
                    </Button>
                    <LoadingButton
                        variant="contained"
                        onClick={handleExcelExport}
                        loading={exportLoading}
                        disabled={data.length === 0}
                    >
                        Экспорт в Excel
                    </LoadingButton>
                </Stack>

                <table className="ant-table">
                    <thead className="ant-table-thead">
                        <tr className="ant-table-row">
                            <th className="ant-table-cell">№</th>
                            <th className="ant-table-cell">Договор</th>
                            <th className="ant-table-cell">Филиал</th>
                            <th className="ant-table-cell">Районное отделение</th>
                            <th className="ant-table-cell">Дата создания</th>
                            <th className="ant-table-cell">Номер постановления</th>
                            <th className="ant-table-cell">Количество показателей в постановлении</th>
                            <th className="ant-table-cell">АКТ</th>
                            <th className="ant-table-cell">Количество подписанных протоколов</th>
                            <th className="ant-table-cell">Количество неподписанных протоколов</th>
                            <th className="ant-table-cell">Лаборатория</th>
                            <th className="ant-table-cell">Заявка</th>
                            <th className="ant-table-cell">Количество исследований</th>
                            <th className="ant-table-cell">Количество исследований в тарификаторе</th>
                            <th className="ant-table-cell">Сумма</th>
                        </tr>
                    </thead>
                    <tbody className="ant-table-tbody">
                                                {groupedData && groupedData.map((group, groupIndex) => {
                            return (group.requests.map((request, requestIndex) => (
                                        <tr key={`${groupIndex}-${requestIndex}`} className="ant-table-row" style={{backgroundColor: "#fafafa"}}>
                                                                                        {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length} style={{verticalAlign: 'top'}}>{groupIndex + 1}</td> : null}
                                                                                        {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length} style={{verticalAlign: 'top'}}>{group.governmentContractName || ""}</td> : null}
                                                                                        {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length} style={{verticalAlign: 'top'}}>{group.filialName || ""}</td> : null}
                                            {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length} style={{verticalAlign: 'top'}}>{group.filialDivisionName || ""}</td> : null}
                                            {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length} style={{verticalAlign: 'top'}}>{group.createdAt ? formatDate(group.createdAt, "DD.MM.YYYY") : ""}</td> : null}
                                            {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length} style={{verticalAlign: 'top'}}>{group.resolutionNumber || ""}</td> : null}
                                            {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length} style={{verticalAlign: 'top'}}>{group.indicatorsCount || 0}</td> : null}
                                            {requestIndex == 0 ? <td className="ant-table-cell" rowSpan={group.requests.length} style={{verticalAlign: 'top'}}>{group.actNumber || ""}</td> : null}                                            
                                            <td className="ant-table-cell">{request.signedProtocols || 0}</td>
                                            <td className="ant-table-cell">{request.notSignedProtocols || 0}</td>
                                            <td className="ant-table-cell">{request.departmentName || ""}</td>
                                            <td className="ant-table-cell">{request.number || 0}</td>
                                            <td className="ant-table-cell">{request.quantityByRequest || 0}</td>
                                            <td className="ant-table-cell">{request.quantityByTarification || 0}</td>
                                            <td className="ant-table-cell">{safeRoundTo2(request.sum || 0)}</td>
                                        </tr>
                                    ))
                            );
                        })}
                        {groupedData.length > 0 && <tr className="ant-table-row" style={{backgroundColor:"#3cb37188"}}>
                            <td></td>
                            <td></td>
                            <td>Всего:</td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td className="ant-table-cell">{sum(groupedData, _ => _.signedProtocols || 0)}</td>
                            <td className="ant-table-cell">{sum(groupedData, _ => _.notSignedProtocols || 0)}</td>
                            <td></td>
                            <td></td>
                            <td className="ant-table-cell">{sum(groupedData, _ => _.quantityByRequest || 0)}</td>
                            <td className="ant-table-cell">{sum(groupedData, _ => _.quantityByTarification || 0)}</td>
                            <td className="ant-table-cell">{safeRoundTo2(sum(groupedData, _ => _.sum || 0))}</td>
                        </tr>}
                    </tbody>
                </table>
            </div>
        </DashboardLayout>
    )
};

export default ResolutionsSummary; 